import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import {
  useInviteEligibility,
  type InviteEligibilityApplicant,
} from '@/modules/application-tracker/api';
import { applicantEmailValidate } from '@/modules/application-tracker/components/lease-form';
import { useDebounce } from '@/shared/hooks';
import { SplitFeatureFlag, useSplitFeatureFlag } from '@/shared/providers/feature-flags';
import { useCurrentUser } from '@/stores/useCurrentUser';

import type { InviteLeaseDetailsFormValues } from '../invite-lease-details.form-values';
import {
  INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE,
  INVITE_ELIGIBILITY_BLOCK_FIELD_MESSAGE,
} from '../utils/submit-errors.util';

export const INVITE_ELIGIBILITY_DEBOUNCE_MS = 750;
export { INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE } from '../utils/submit-errors.util';

type EligibilityEmailEntry = {
  formIndex: number;
  email: string;
};

type EligibilityInputs = {
  propertyId: string;
  addressId: string;
  unit: string;
  emailEntries: EligibilityEmailEntry[];
  emails: string[];
};

type SyncEligibilityErrorsArgs = {
  syncFieldErrors: boolean;
  isFeatureFlagEnabled: boolean;
  isStale: boolean;
  rowCount: number;
  applicantsByFormIndex: Map<number, InviteEligibilityApplicant>;
  setError: ReturnType<typeof useFormContext>['setError'];
  clearErrors: ReturnType<typeof useFormContext>['clearErrors'];
  getFieldState: ReturnType<typeof useFormContext>['getFieldState'];
};

const isClientValidEmail = (email: string): boolean => applicantEmailValidate.isValidSync(email);

const trimOrEmpty = (value: string | undefined): string => value?.trim() || '';

const resolvePropertyId = (address: InviteLeaseDetailsFormValues['address'] | undefined): string =>
  trimOrEmpty(address?.property_alpha_id) || trimOrEmpty(address?.property_id);

const resolveAddressId = (address: InviteLeaseDetailsFormValues['address'] | undefined): string =>
  trimOrEmpty(address?.address_alpha_id) || trimOrEmpty(address?.address_id);

const collectValidEmails = (
  applicants: InviteLeaseDetailsFormValues['applicants'] | undefined,
): EligibilityEmailEntry[] => {
  if (!applicants?.length) {
    return [];
  }

  const entries: EligibilityEmailEntry[] = [];
  applicants.forEach((row, formIndex) => {
    const email = trimOrEmpty(row?.email ?? undefined).toLowerCase();
    if (email && isClientValidEmail(email)) {
      entries.push({ formIndex, email });
    }
  });
  return entries;
};

const buildEligibilityInputs = (
  address: InviteLeaseDetailsFormValues['address'] | undefined,
  unit: InviteLeaseDetailsFormValues['unit'] | undefined,
  applicants: InviteLeaseDetailsFormValues['applicants'] | undefined,
): EligibilityInputs => {
  const emailEntries = collectValidEmails(applicants);
  return {
    propertyId: resolvePropertyId(address),
    addressId: resolveAddressId(address),
    unit: typeof unit === 'string' ? unit.trim() : '',
    emailEntries,
    emails: emailEntries.map((entry) => entry.email),
  };
};

const inputsKey = (inputs: EligibilityInputs): string =>
  JSON.stringify({
    propertyId: inputs.propertyId,
    addressId: inputs.addressId,
    unit: inputs.unit,
    emails: inputs.emails,
  });

const emailFieldPath = (formIndex: number) => `applicants.${formIndex}.email`;

const mapApplicantsByFormIndex = (
  responseApplicants: InviteEligibilityApplicant[] | undefined,
  emailEntries: EligibilityEmailEntry[],
): Map<number, InviteEligibilityApplicant> => {
  const map = new Map<number, InviteEligibilityApplicant>();
  if (!responseApplicants) {
    return map;
  }

  for (const applicant of responseApplicants) {
    const entry = emailEntries[applicant.index];
    if (entry) {
      map.set(entry.formIndex, applicant);
    }
  }
  return map;
};

const hasBlockOutcome = (
  applicantsByFormIndex: Map<number, InviteEligibilityApplicant>,
): boolean => {
  for (const applicant of applicantsByFormIndex.values()) {
    if (applicant.outcome === 'block') {
      return true;
    }
  }
  return false;
};

const canRunEligibilityQuery = (
  isFeatureFlagEnabled: boolean,
  inputs: EligibilityInputs,
): boolean =>
  isFeatureFlagEnabled &&
  Boolean(inputs.propertyId) &&
  Boolean(inputs.unit) &&
  inputs.emails.length > 0;

/**
 * Keep the last successful result while a background refetch is in flight.
 * Only treat as stale when inputs moved, the query is off, or we have no success yet / errored.
 */
const hasFreshEligibilityResult = ({
  canQuery,
  inputsMatch,
  isSuccess,
  isError,
}: {
  canQuery: boolean;
  inputsMatch: boolean;
  isSuccess: boolean;
  isError: boolean;
}): boolean => canQuery && inputsMatch && isSuccess && !isError;

/** Block and warning both get a red email field; banner carries the copy. */
const shouldHighlightEligibilityEmail = (
  outcome: InviteEligibilityApplicant['outcome'] | undefined,
): boolean => outcome === 'block' || outcome === 'warning';

const syncEligibilityFieldErrors = ({
  syncFieldErrors,
  isFeatureFlagEnabled,
  isStale,
  rowCount,
  applicantsByFormIndex,
  setError,
  clearErrors,
  getFieldState,
}: SyncEligibilityErrorsArgs): void => {
  if (!syncFieldErrors) {
    return;
  }

  const clearEligibilityError = (formIndex: number) => {
    const path = emailFieldPath(formIndex);
    if (getFieldState(path).error?.type === INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
      clearErrors(path);
    }
  };

  const applyEligibilityHighlight = (formIndex: number) => {
    const path = emailFieldPath(formIndex);
    const existingType = getFieldState(path).error?.type;
    // Leave required, format, and duplicate-email errors in place.
    if (existingType && existingType !== INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
      return;
    }
    // Idempotent: Yup `mode: 'all'` wipes manual setError; re-apply without looping.
    if (existingType !== INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
      setError(path, {
        type: INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE,
        message: INVITE_ELIGIBILITY_BLOCK_FIELD_MESSAGE,
      });
    }
  };

  if (!isFeatureFlagEnabled || isStale) {
    for (let formIndex = 0; formIndex < rowCount; formIndex += 1) {
      clearEligibilityError(formIndex);
    }
    return;
  }

  for (let formIndex = 0; formIndex < rowCount; formIndex += 1) {
    const applicant = applicantsByFormIndex.get(formIndex);
    if (shouldHighlightEligibilityEmail(applicant?.outcome)) {
      applyEligibilityHighlight(formIndex);
    } else {
      clearEligibilityError(formIndex);
    }
  }
};

export type UseInviteEligibilityCheckResult = {
  isFeatureEnabled: boolean;
  /** True when banners/errors should not reflect cached data (inputs moved, no success, or error). */
  isStale: boolean;
  hasBlock: boolean;
  getApplicant: (formIndex: number) => InviteEligibilityApplicant | undefined;
};

/**
 * Debounced invite eligibility preflight for the shared invite form.
 * Call from components under `<LeaseDetailsForm>`'s FormProvider.
 */
export const useInviteEligibilityCheck = (
  options: { syncFieldErrors?: boolean } = {},
): UseInviteEligibilityCheckResult => {
  const { syncFieldErrors = false } = options;
  const { user } = useCurrentUser();
  // Split targets this flag by operator company (traffic key), not the anonymous browser id.
  const operatorCompanyId = user?.company?.operator_company_id;
  const { isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.LLX_5050_INVITE_ELIGIBILITY,
    operatorCompanyId ? { splitKey: operatorCompanyId } : undefined,
  );
  const { setError, clearErrors, getFieldState } = useFormContext();
  // Subscribe so we re-apply block field errors after Yup revalidation clears them.
  const { errors, isValidating } = useFormState();

  const address = useWatch({ name: 'address' }) as InviteLeaseDetailsFormValues['address'];
  const unit = useWatch({ name: 'unit' }) as InviteLeaseDetailsFormValues['unit'];
  const applicants = useWatch({ name: 'applicants' }) as InviteLeaseDetailsFormValues['applicants'];

  const liveInputs = useMemo(
    () => buildEligibilityInputs(address, unit, applicants),
    [address, unit, applicants],
  );
  const debouncedInputs = useDebounce(liveInputs, INVITE_ELIGIBILITY_DEBOUNCE_MS);
  const canQuery = canRunEligibilityQuery(isFeatureFlagEnabled, debouncedInputs);

  const query = useInviteEligibility({
    propertyId: debouncedInputs.propertyId || undefined,
    addressId: debouncedInputs.addressId || undefined,
    unit: debouncedInputs.unit || undefined,
    emails: debouncedInputs.emails,
    enabled: canQuery,
  });

  // Disabled queries can keep prior data; require success for the current inputs (refetch ok).
  const hasFreshResult = hasFreshEligibilityResult({
    canQuery,
    inputsMatch: inputsKey(liveInputs) === inputsKey(debouncedInputs),
    isSuccess: query.isSuccess,
    isError: query.isError,
  });
  const isStale = !hasFreshResult;

  const applicantsByFormIndex = useMemo(() => {
    if (!hasFreshResult) {
      return new Map<number, InviteEligibilityApplicant>();
    }
    return mapApplicantsByFormIndex(query.data?.applicants, debouncedInputs.emailEntries);
  }, [hasFreshResult, query.data, debouncedInputs.emailEntries]);

  const hasBlock = hasBlockOutcome(applicantsByFormIndex);

  useEffect(() => {
    if (isValidating) {
      return;
    }
    syncEligibilityFieldErrors({
      syncFieldErrors,
      isFeatureFlagEnabled,
      isStale,
      rowCount: applicants?.length ?? 0,
      applicantsByFormIndex,
      setError,
      clearErrors,
      getFieldState,
    });
  }, [
    syncFieldErrors,
    isFeatureFlagEnabled,
    isStale,
    applicants?.length,
    applicantsByFormIndex,
    setError,
    clearErrors,
    getFieldState,
    errors,
    isValidating,
  ]);

  return {
    isFeatureEnabled: isFeatureFlagEnabled,
    isStale,
    hasBlock,
    getApplicant: (formIndex: number) => applicantsByFormIndex.get(formIndex),
  };
};
